import { Suspense, forwardRef, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { enqueueSnackbar } from "notistack"
import { FormikProps } from "formik"
import { CreateUpdateDepartment } from "../departmentsTypes"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { CompanyAdminDepartmentsQuery, createDepartmentForCompany } from "../departmentsApi"
import { CreateUpdateDepartmentForm } from "../components/CreateUpdateDepartmentForm"
import { COMPANY_ADMIN_ORGANIZATION_DEPARTMENTS_ROUTE, COMPANY_ADMIN_ROUTE } from "../../../../../../constants/routes"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectEffectiveCompanyId } from "../../../../../context/contextSelectors"

const Department = forwardRef<FormikProps<CreateUpdateDepartment>>((props, ref) => {
    const companyId = useAppSelector(selectEffectiveCompanyId)
    const navigate = useNavigate()

    const { mutateAsync: callCreateDepartment } = useApiMutation(createDepartmentForCompany, {
        async onSuccess(params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [CompanyAdminDepartmentsQuery.LIST] })
            if ("serviceResponse" in data && data.serviceResponse.displayMessage) {
                enqueueSnackbar({
                    variant: "success",
                    message: data?.serviceResponse.displayMessage,
                })
            }
            navigate(COMPANY_ADMIN_ORGANIZATION_DEPARTMENTS_ROUTE)
        },
    })

    return (
        <>
            <CreateUpdateDepartmentForm
                ref={ref}
                onSubmit={(data) => {
                    companyId && callCreateDepartment({ department: data, companyId: companyId })
                }}
            />
        </>
    )
})

export function CreateDepartment() {
    const departmentFormRef = useRef<FormikProps<CreateUpdateDepartment>>(null)

    const handleSaveClick = () => {
        if (departmentFormRef.current) {
            departmentFormRef.current?.submitForm()
        }
    }

    return (
        <>
            <LocalizedStrict id="create-department-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.COMPANY_ADMIN_ORGANIZATION_DEPARTMENTS_CREATE}
                    title="Create department"
                    rightElement={
                        <LocalizedStrict id="create-department-save-button">
                            <StandardButton type="submit" onClick={handleSaveClick}>
                                Create
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="company-nav-item-home">
                            <BreadcrumbLink to={COMPANY_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-organization-title">
                            <Breadcrumb>Organization</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-organization-departments-title">
                            <BreadcrumbLink to={COMPANY_ADMIN_ORGANIZATION_DEPARTMENTS_ROUTE}>
                                Departments
                            </BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-organization-departments-create-title">
                            <Breadcrumb>Create department</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <Department ref={departmentFormRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}
